import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft } from 'lucide-react';

function PrivacyPage({ Header, Footer }) {
    const handleEmailClick = (e) => {
        e.preventDefault();
        window.location.href = 'mailto:service@8004agents.ai';
    };

    return (
        <div className="min-h-screen bg-[#f8f9fa]">
            <Helmet>
                <title>Privacy Policy | ERC-8004 Explorer</title>
                <meta name="description" content="Privacy Policy for ERC-8004 Explorer. Learn how we collect, use, and protect your information." />
                <link rel="canonical" href="https://8004agents.ai/privacy" />
            </Helmet>

            <Header
                stats={null}
                chainFilter="all"
                onChainFilterChange={() => { }}
                chains={[]}
                refreshing={false}
                onRefresh={() => { }}
            />

            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
                <Link
                    to="/"
                    className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition"
                >
                    <ArrowLeft className="w-4 h-4" />
                    Back to Explorer
                </Link>
            </div>

            <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
                <div className="bg-white rounded-xl sm:rounded-2xl border border-gray-200 p-6 sm:p-10">
                    <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Privacy Policy</h1>
                    <p className="text-sm text-gray-500 mb-8">Last updated: January 2025</p>

                    <div className="prose prose-gray max-w-none space-y-6">
                        <section>
                            <h2 className="text-lg font-semibold text-gray-900 mb-3">1. Introduction</h2>
                            <p className="text-gray-600 leading-relaxed">
                                ERC-8004 Explorer ("we," "our," or "us") operates the website 8004agents.ai. This Privacy Policy explains how we collect, use, and protect information when you use our service.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-lg font-semibold text-gray-900 mb-3">2. Information We Collect</h2>
                            <p className="text-gray-600 leading-relaxed mb-3">We collect minimal information to provide and improve our service:</p>
                            <ul className="list-disc pl-5 text-gray-600 space-y-2">
                                <li><strong>Email addresses:</strong> Only if you voluntarily subscribe to our newsletter</li>
                                <li><strong>Usage data:</strong> Anonymous analytics about how visitors use our site (pages visited, time spent)</li>
                                <li><strong>Blockchain data:</strong> We index publicly available on-chain data about ERC-8004 agents</li>
                            </ul>
                        </section>

                        <section>
                            <h2 className="text-lg font-semibold text-gray-900 mb-3">3. How We Use Your Information</h2>
                            <ul className="list-disc pl-5 text-gray-600 space-y-2">
                                <li>To send newsletter updates (only if you subscribed)</li>
                                <li>To improve our website and user experience</li>
                                <li>To monitor and analyze usage patterns</li>
                                <li>To detect and prevent technical issues</li>
                            </ul>
                        </section>

                        <section>
                            <h2 className="text-lg font-semibold text-gray-900 mb-3">4. Third-Party Services</h2>
                            <p>
                                We use third-party service providers to support functionality such as:
                            </p>
                            <ul>
                                <li>Website hosting and content delivery</li>
                                <li>Backup data storage</li>
                                <li>Analytics and performance monitoring</li>
                                <li>Blockchain network access for reading public on-chain data</li>
                            </ul>
                            <p>
                                These providers process data only as necessary to perform their services and are subject
                                to their own privacy policies and security practices.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-lg font-semibold text-gray-900 mb-3">5. Cookies</h2>
                            <p className="text-gray-600 leading-relaxed">
                                We may use cookies and similar tracking technologies to improve your experience. You can instruct your browser to refuse all cookies or indicate when a cookie is being sent.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-lg font-semibold text-gray-900 mb-3">6. Data Security</h2>
                            <p className="text-gray-600 leading-relaxed">
                                Data Security

                                Public blockchain data is not created or controlled by us and is inherently transparent on the blockchain. We collect anonymized analytics to understand website usage and improve your experience. Any non-public information you provide is protected using reasonable administrative and technical safeguards. We retain information only as necessary and do not collect passwords, private keys, or other sensitive credentials. Third-party services we use follow their own security and privacy practices.

                            </p>
                        </section>

                        <section>
                            <h2 className="text-lg font-semibold text-gray-900 mb-3">7. Your Rights</h2>
                            <p className="text-gray-600 leading-relaxed mb-3">You have the right to:</p>
                            <ul className="list-disc pl-5 text-gray-600 space-y-2">
                                <li>Unsubscribe from our newsletter at any time</li>
                                <li>Request deletion of your email from our database</li>
                                <li>Access information we hold about you</li>
                            </ul>
                        </section>

                        <section>
                            <h2 className="text-lg font-semibold text-gray-900 mb-3">8. Children's Privacy</h2>
                            <p className="text-gray-600 leading-relaxed">
                                Our service is not directed to anyone under 13. We do not knowingly collect personal information from children under 13.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-lg font-semibold text-gray-900 mb-3">9. Changes to This Policy</h2>
                            <p className="text-gray-600 leading-relaxed">
                                We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new policy on this page and updating the "Last updated" date.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-lg font-semibold text-gray-900 mb-3">10. Contact Us</h2>
                            <p className="text-gray-600 leading-relaxed">
                                If you have questions about this Privacy Policy, please contact us at{' '}
                                <button
                                    onClick={handleEmailClick}
                                    className="text-blue-600 hover:text-blue-700 underline"
                                >
                                    service@8004agents.ai
                                </button>
                            </p>
                        </section>
                    </div>
                </div>
            </main>

            <Footer stats={null} agents={[]} />
        </div>
    );
}

export default PrivacyPage;